import { Trans } from '@lingui/macro';
import AspenDigital from 'assets/logos/partners/aspen_digital_logo.webp';
import Madworld from 'assets/logos/partners/madworld_logo.webp';
import Olivex from 'assets/logos/partners/olivex_logo.webp';
import { ButtonPrimary } from 'components/Button';
import Socials from 'components/Socials/Socials';
import Spacer from 'components/Spacer';
import { TextInput } from 'components/TextInput/TextInput';
import { PaperPlaneRight } from 'phosphor-react';
import React, { useState } from 'react';
import { useSignupNewsletterMutation } from 'state/newsletter/api';
import styled from 'styled-components/macro';

const HomeFooterWrapper = styled.div``;

const FooterContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100px;
  background: rgb(0, 0, 0);
  padding: 21px 80px 31px;

  ${({ theme }) => theme.mediaWidth.upToLarge`
    padding: 21px 30px 31px;
  `};
  ${({ theme }) => theme.mediaWidth.upToMedium`
    flex-direction: column;
    height: 400px;
    padding: 21px 0 40px;
  `};
`;

const SignupWrapper = styled.form`
  display: flex;
  align-items: center;
  ${({ theme }) => theme.mediaWidth.upToMedium`
    flex-direction: column;
    width: 100%;
    max-width: 400px;
    padding: 0 30px;
  `};
`;
const SignupLabel = styled.h3`
  font-family: 'Presicav';
  font-weight: bold;
  text-align: right;
  padding-right: 30px;
  white-space: nowrap;

  ${({ theme }) => theme.mediaWidth.upToLarge`
    font-size: 12px;
  `};

  ${({ theme }) => theme.mediaWidth.upToMedium`
    text-align: center;
    padding-bottom: 10px;
    padding-right: 0;
    font-size: 1.07em;
  `};
`;
const EmailInput = styled(TextInput)`
  height: fit-content;
  min-width: 200px;
  max-width: 300px;
  input {
    border-radius: 8px;
    padding: 10px 14px;
    line-height: 1.2;

    ${({ theme }) => theme.mediaWidth.upToMedium`
      padding: 10px 14px;
      box-shadow: inset 0 3px 5px #00000090;
      ::placeholder {
        text-align: center; 
      }
    `};
    &::placeholder {
      font-family: 'Presicav';
    }
  }

  ${({ theme }) => theme.mediaWidth.upToMedium`
    width: 100%;
    padding-bottom: 15px;
  `};
`;
const SubmitButtonWrapper = styled.div`
  width: 100%;
  padding: 15px 10px;
  ${({ theme }) => theme.mediaWidth.upToMedium`
    padding: 15px 30px;
  `};
`;

const SubmitMobile = styled.div`
  display: none;
  ${({ theme }) => theme.mediaWidth.upToMedium`
    display: block;
  `};
`;
const SubmitDesktop = styled.div`
  display: block;
  ${({ theme }) => theme.mediaWidth.upToMedium`
    display: none;
  `};
`;
const SubmitButton = styled(ButtonPrimary)`
  padding-top: 8px;
  padding-bottom: 5px;
  ${({ theme }) => theme.mediaWidth.upToMedium`
    padding: 12px;
  `};
`;

const TermsWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0 10px;

  font-size: 12px;
`;
const TermsLink = styled.a`
  color: #ffffff;
  text-decoration: none;
  white-space: nowrap;
`;

const PartnersWrapper = styled.div`
  display: flex;
  align-items: center;
  ${({ theme }) => theme.mediaWidth.upToMedium`
    width: 100%;
    flex-wrap: wrap;
    justify-content: center;
  `};
`;
const PartnerLink = styled.a``;
const PartnerLogo = styled.img`
  height: 32px;
  width: auto;
  margin: 0 15px;

  ${({ theme }) => theme.mediaWidth.upToLarge`
    height: 26px;
  `};
  ${({ theme }) => theme.mediaWidth.upToMedium`
    height: 25px;
    margin: 10px 15px;
  `};
`;

const ShowExtraLargeOnly = styled.div`
  display: block;
  ${({ theme }) => theme.mediaWidth.upToLarge`
    display: none;
  `};
`;

const TermsUpToLarge = styled.div`
  display: none;
  ${({ theme }) => theme.mediaWidth.upToLarge`
    width: 100%;
    display: flex;
    justify-content: center;
    background: rgb(0, 0, 0);
    padding: 0px 10px 20px;
  `};
  ${({ theme }) => theme.mediaWidth.upToMedium`
    padding: 50px 10px 50px;
  `};
`;

const SocialsWrapper = styled.div`
  display: none;
  ${({ theme }) => theme.mediaWidth.upToMedium`
    padding: 40px 0 40px 0;
    display: block;
  `};
`;

const Partners: React.FC = () => {
  return (
    <PartnersWrapper>
      <PartnerLink href="https://www.olivex.ai/" target="_blank" rel="noreferrer">
        <PartnerLogo src={Olivex} alt="OliveX Logo" />
      </PartnerLink>
      <PartnerLink href="https://madworld.io/" target="_blank" rel="noreferrer">
        <PartnerLogo src={Madworld} alt="Madworld Logo" />
      </PartnerLink>
      <PartnerLink href="https://www.aspendigital.co/" target="_blank" rel="noreferrer">
        <PartnerLogo src={AspenDigital} alt="Aspen Digital Logo" />
      </PartnerLink>
    </PartnersWrapper>
  );
};

const Terms: React.FC = () => {
  return (
    <TermsWrapper>
      <TermsLink href="https://breathonics.com/privacy" target="_blank" rel="noreferrer">
        <Trans>Privacy Policy</Trans>
      </TermsLink>
      <Spacer axis="horizontal" size={20} />
      <TermsLink href="https://breathonics.com/terms" target="_blank" rel="noreferrer">
        <Trans>Terms</Trans>
      </TermsLink>
    </TermsWrapper>
  );
};

const Signup: React.FC = () => {
  const [email, setEmail] = useState('');

  const [signupNewsletter, { isLoading }] = useSignupNewsletterMutation();

  return (
    <SignupWrapper
      onSubmit={(e) => {
        signupNewsletter({ email });
        e.preventDefault();
      }}
    >
      <SignupLabel>
        <Trans>
          Once you enter your email
          <br />
          there is no turning back
        </Trans>
      </SignupLabel>
      <EmailInput
        value={email}
        onUserInput={(value: string) => {
          setEmail(value);
        }}
        placeholder={'Email...'}
        fontSize={'14px'}
        type="email"
      />
      <SubmitButtonWrapper>
        <SubmitButton disabled={isLoading || email === ''} type="submit">
          <SubmitMobile>
            <Trans>SUBMIT</Trans>
          </SubmitMobile>
          <SubmitDesktop>
            <PaperPlaneRight size={20} weight="fill" />
          </SubmitDesktop>
        </SubmitButton>
      </SubmitButtonWrapper>
    </SignupWrapper>
  );
};

const HomeFooter: React.FC = () => {
  return (
    <HomeFooterWrapper>
      <FooterContent>
        <Signup />
        <ShowExtraLargeOnly>
          <Terms />
        </ShowExtraLargeOnly>
        <SocialsWrapper>
          <Socials />
        </SocialsWrapper>
        <Partners />
      </FooterContent>
      <TermsUpToLarge>
        <Terms />
      </TermsUpToLarge>
    </HomeFooterWrapper>
  );
};

export default HomeFooter;
